/**
 * stampDuty.ts — the duty on buying a home, from the schedules in constants/stampDuty.ts.
 *
 * One function, used by the Strategy Analysis Setup modal, Manual Control and the Downsizer module to
 * pre-fill an ESTIMATE the client can overwrite. Where no state is chosen it returns null, never a
 * guess: a blank is said out loud on every screen and in the advice document, in the same way a
 * blank health answer is (owner, 23 Sep 2026).
 */
import { STAMP_DUTY_SCHEDULES, type StampDutyBand, type StampDutyState } from './constants/stampDuty.ts';

export type { StampDutyState } from './constants/stampDuty.ts';
export { STAMP_DUTY_STATES, STAMP_DUTY_SCHEDULES, STAMP_DUTY_PROVENANCE } from './constants/stampDuty.ts';

export function isStampDutyState(value: unknown): value is StampDutyState {
    return typeof value === 'string' && value in STAMP_DUTY_SCHEDULES;
}

function dutyFromBands(bands: StampDutyBand[], value: number): number {
    let band = bands[0]!;
    for (const b of bands) if (value > b.from) band = b;
    if (band.flatOnWhole) return band.rate * value;
    return band.base + band.rate * (value - band.from);
}

/** A home to live in, or a property bought to rent out (the general rates where a state has them). */
export type StampDutyUse = 'home' | 'investment';

/**
 * The duty on a purchase at `price` in `state`, to the nearest dollar; null where the state is not
 * one we hold a schedule for, or the price is not a positive number.
 */
export function calculateStampDuty(state: unknown, price: unknown, use: StampDutyUse = 'home'): number | null {
    if (!isStampDutyState(state)) return null;
    const value = typeof price === 'number' ? price : parseFloat(String(price ?? ''));
    if (!Number.isFinite(value) || value <= 0) return null;
    const schedule = STAMP_DUTY_SCHEDULES[state];

    let duty: number;
    if (use === 'investment' && schedule.investor) {
        duty = dutyFromBands(schedule.investor.bands, value);
    } else if (schedule.formulaBelow && value <= schedule.formulaBelow.threshold) {
        const v = value / 1000;
        duty = schedule.formulaBelow.a * v * v + schedule.formulaBelow.b * v;
    } else if (schedule.generalAbove && value > schedule.generalAbove.above) {
        duty = dutyFromBands(schedule.generalAbove.bands, value);
    } else {
        duty = dutyFromBands(schedule.bands, value);
    }
    if (schedule.minimum != null) duty = Math.max(schedule.minimum, duty);
    return Math.round(duty);
}

/**
 * The dearest property a budget buys once the duty on it is paid: the price p with p + duty(p) equal
 * to the budget, to the dollar. Duty rises with price, so a handful of fixed-point passes settle it.
 *
 * Built for the village and downsizer engines, which decide how much of a home sale goes into a
 * property (owner, 24 Sep 2026: "there is no good reason stamp duty should not be modelled on a
 * bought investment property"). Without a state there is no duty to strike and the whole budget is
 * the price — no state, no guess — and the caller says so on the card.
 */
export function purchaseWithinBudget(state: unknown, budget: unknown, use: StampDutyUse = 'home'): { price: number; duty: number } {
    const total = typeof budget === 'number' ? budget : parseFloat(String(budget ?? ''));
    if (!Number.isFinite(total) || total <= 0) return { price: 0, duty: 0 };
    if (!isStampDutyState(state)) return { price: Math.round(total), duty: 0 };
    let price = total;
    for (let i = 0; i < 12; i++) {
        const duty = calculateStampDuty(state, price, use) ?? 0;
        const next = total - duty;
        if (Math.abs(next - price) < 0.5) { price = next; break; }
        price = next;
    }
    price = Math.max(0, Math.round(price));
    let duty = calculateStampDuty(state, price, use) ?? 0;
    // Never over the budget, to the dollar.
    while (price > 0 && price + duty > total) { price -= 1; duty = calculateStampDuty(state, price, use) ?? 0; }
    return { price, duty };
}

/** The one-line description a screen shows beside the estimate. */
export function describeStampDutyScale(state: unknown, use: StampDutyUse = 'home'): string | null {
    if (!isStampDutyState(state)) return null;
    const s = STAMP_DUTY_SCHEDULES[state];
    const scale = use === 'investment' && s.investor ? s.investor.scale : s.scale;
    return `${scale}. No concessions applied; an estimate to overwrite from a conveyancer's quote.`;
}
